import { Badge, Button, Dialog, Field, Input, Select, toast } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ASPECT_RATIO_STRATEGIES,
  useApi,
  type AspectRatioStrategy,
  type OrderDTO,
  type OrderDispatchRequest,
  type ProductionJobDTO,
} from "../../api";

interface LineDraft {
  lineItemId: string;
  sku: string;
  titleText: string;
  vendor: string;
  artworkId: string;
  productId: string;
  aspectRatioStrategy: AspectRatioStrategy;
  productionCenterId: string;
  quantity: number;
  maxQuantity: number;
  matched: boolean;
}

/**
 * Sends an order's outstanding line items to production.
 *
 * Each line is pre-matched through the SKU-match table; a line with no match
 * shows "No match" and has to be pointed at an artwork by hand. The production
 * centre list narrows as soon as a product is picked — only centres that ship
 * to the order's destination *and* carry that product can take the job, which
 * is the rule the resolver applies server-side too.
 */
export function DispatchDialog({
  open,
  onClose,
  order,
  existingJobs,
  onDispatched,
}: {
  open: boolean;
  onClose: () => void;
  order: OrderDTO | null;
  existingJobs: ProductionJobDTO[];
  onDispatched: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [busy, setBusy] = useState(false);

  const countryCode = order?.order.shippingAddress?.countryCodeV2 ?? "";

  const artworks = useQuery({
    queryKey: ["artwork", "core"],
    queryFn: () => api.artworkCores.findAll(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const products = useQuery({
    queryKey: ["product"],
    queryFn: () => api.products.findAll(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const centers = useQuery({
    queryKey: ["production", "center", "country", countryCode],
    queryFn: () => api.productionCenters.getAvailableByCountry(countryCode),
    enabled: open && Boolean(countryCode),
  });

  useEffect(() => {
    if (!open || !order) return;

    let cancelled = false;
    void (async () => {
      const items = order.order.lineItems?.nodes ?? [];
      const drafts: LineDraft[] = [];

      for (const item of items) {
        // Quantities already sent to production don't need sending again.
        let dispatched = 0;
        for (const job of existingJobs) {
          for (const line of job.lines ?? []) {
            if (line.line_item_id === item.id) dispatched += line.quantity;
          }
        }
        const remaining = (item.unfulfilledQuantity ?? item.quantity ?? 0) - dispatched;
        if (remaining <= 0) continue;

        const sku = item.variant?.sku ?? "";
        let artworkId = "";
        let productId = "";
        if (sku) {
          try {
            const match = await api.skuMatches.getBySku(sku);
            if (match) {
              artworkId = String(match.artwork_id);
              productId = String(match.product_id);
            }
          } catch {
            // A missing match is the normal "no match" case, not an error.
          }
        }

        drafts.push({
          lineItemId: item.id,
          sku,
          titleText: item.variant?.title ?? item.title ?? "",
          vendor: item.vendor ?? "",
          artworkId,
          productId,
          aspectRatioStrategy: "RESIZE",
          productionCenterId: "",
          quantity: remaining,
          maxQuantity: remaining,
          matched: Boolean(artworkId),
        });
      }

      if (!cancelled) setLines(drafts);
    })();

    return () => {
      cancelled = true;
    };
  }, [api, existingJobs, open, order]);

  const update = (index: number, changes: Partial<LineDraft>) =>
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...changes } : line)));

  const pickProduct = async (index: number, productId: string) => {
    update(index, { productId, productionCenterId: "" });
    if (!productId || !countryCode) return;
    try {
      const available = await api.productionCenters.getAvailable(countryCode, Number(productId));
      if (available.length > 0) {
        update(index, { productionCenterId: String(available[0]!.id) });
      }
    } catch {
      // Leave the centre unset; the operator picks one from the full list.
    }
  };

  const submit = async () => {
    if (!order) return;
    const requests: OrderDispatchRequest[] = lines
      .filter((line) => line.productionCenterId && line.quantity > 0)
      .map((line) => ({
        order_header_id: order.id,
        line_item_id: line.lineItemId,
        line_item_sku: line.sku,
        line_item_title: line.titleText,
        line_item_vendor: line.vendor,
        artwork_id: line.artworkId ? Number(line.artworkId) : null,
        product_id: line.productId ? Number(line.productId) : null,
        aspect_ratio_strategy: line.aspectRatioStrategy,
        production_center_id: Number(line.productionCenterId),
        quantity: line.quantity,
      }));

    if (requests.length === 0) {
      toast.info(t("notification.order-dispatch.empty", "There is nothing to dispatch"));
      return;
    }

    setBusy(true);
    try {
      const created = await api.productionJobs.dispatch(requests);
      if (created.length === 0) {
        toast.error(t("notification.order-dispatch.error", "Failed to dispatch order"));
      } else {
        toast.success(t("notification.order-dispatch.success", "Order dispatched successfully"));
        onDispatched();
        onClose();
      }
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.order-dispatch.error", "Failed to dispatch order"),
      );
    } finally {
      setBusy(false);
    }
  };

  const artworkOptions = (artworks.data ?? [])
    .map((entry) => ({ value: String(entry.id), label: entry.metadata.code }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const productOptions = (products.data ?? [])
    .map((entry) => ({ value: String(entry.id), label: entry.code }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const centerOptions = (centers.data ?? []).map((entry) => ({
    value: String(entry.id),
    label: entry.name,
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("dialog.order_dispatch.title", "Order dispatch")}
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            {t("dialog.confirm.dispatch", "Dispatch")}
          </Button>
        </>
      }
    >
      {lines.length === 0 ? (
        <p className="text-[13px] text-ink-muted">
          {t("notification.order-dispatch.empty", "There is nothing to dispatch")}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {lines.map((line, index) => (
            <div key={line.lineItemId} className="rounded-lg border border-line p-3">
              <div className="mb-2 flex items-center gap-2">
                <span className="font-medium text-ink">{line.sku || line.titleText}</span>
                <Badge variant={line.matched ? "success" : "error"} size="sm">
                  {line.matched
                    ? t("value.dispatch.match", "Matched")
                    : t("value.dispatch.no-match", "No match")}
                </Badge>
              </div>

              <div className="grid gap-3 md:grid-cols-4">
                <Field label={t("form.field.artwork", "Artwork")}>
                  <Select
                    value={line.artworkId}
                    options={artworkOptions}
                    placeholder={t("form.field.select", "Select")}
                    onChange={(event) => {
                      const value = event.target.value;
                      const artwork = (artworks.data ?? []).find(
                        (entry) => String(entry.id) === value,
                      );
                      update(index, {
                        artworkId: value,
                        matched: Boolean(value),
                        aspectRatioStrategy:
                          artwork?.metadata.aspect_ratio_strategy ?? line.aspectRatioStrategy,
                      });
                    }}
                  />
                </Field>

                <Field label={t("form.field.product", "Product")} required>
                  <Select
                    value={line.productId}
                    options={productOptions}
                    placeholder={t("form.field.select", "Select")}
                    onChange={(event) => void pickProduct(index, event.target.value)}
                  />
                </Field>

                <Field label={t("form.field.production-center", "Production center")} required>
                  <Select
                    value={line.productionCenterId}
                    options={centerOptions}
                    placeholder={t("form.field.select", "Select")}
                    onChange={(event) => update(index, { productionCenterId: event.target.value })}
                  />
                </Field>

                <Field
                  label={t("form.field.quantity", "Quantity")}
                  hint={`/ ${line.maxQuantity}`}
                >
                  <Input
                    type="number"
                    min={0}
                    max={line.maxQuantity}
                    value={line.quantity}
                    onChange={(event) =>
                      update(index, {
                        quantity: Math.min(Number(event.target.value), line.maxQuantity),
                      })
                    }
                  />
                </Field>

                <Field label={t("form.field.aspect-ratio-strategy", "Aspect ratio strategy")}>
                  <Select
                    value={line.aspectRatioStrategy}
                    options={ASPECT_RATIO_STRATEGIES.map((entry) => ({
                      value: entry,
                      label: entry,
                    }))}
                    onChange={(event) =>
                      update(index, {
                        aspectRatioStrategy: event.target.value as AspectRatioStrategy,
                      })
                    }
                  />
                </Field>
              </div>
            </div>
          ))}
        </div>
      )}
    </Dialog>
  );
}
